<template>
  <ModalQmulus
    v-model:visible="visible"
    :closable="!isSubmitting"
    :title="i18n.global.t(`${I18N_PREFIX}.bugReport`)"
    style="
      width: 500px;
      max-width: 100%;
"
    @hide="emit('close')"
  >
    <div class="flex-column">
      <label class="input-wrapper">
        {{ i18n.global.t('generalTerm.fullName') }}
        <InputText
          v-model="feedbackForm.name"
          :disabled="isSubmitting"
          :invalid="!!formattedErrorMap?.name"
          :error-message="formattedErrorMap?.name?._errors[0]"
        />
      </label>
      <label class="input-wrapper">
        {{ i18n.global.t('generalTerm.email') }}
        <InputText
          v-model="feedbackForm.email"
          :disabled="isSubmitting"
          :invalid="!!formattedErrorMap?.email"
          :error-message="formattedErrorMap?.email?._errors[0]"
        />
      </label>
      <label class="input-wrapper">
        {{ i18n.global.t('common.description') }}
        <InputTextarea
          v-model="feedbackForm.message"
          :disabled="isSubmitting"
          :placeholder="i18n.global.t(`${I18N_PREFIX}.descriptionPlaceholder`)"
          :invalid="!!formattedErrorMap?.message"
          :error-message="formattedErrorMap?.message?._errors[0]"
          :rows="7"
          auto-resize
        />
      </label>
      <div class="input-wrapper">
        {{ i18n.global.t(`${I18N_PREFIX}.attachments`) }}
        <UploadFileDragAndDrop
          :accept="MIME_GROUPS.images"
          :max-files="5"
          :max-file-size="MAXIMUM_IMAGE_SIZE"
          @upload-file-list="onAttachmentSelection"
        />
      </div>
    </div>
    <template #actions>
      <SemanticButtonGroup :buttons="actionButtons" />
    </template>
  </ModalQmulus>
</template>

<script setup lang="ts">
import {
  useToast,
} from '@NitrexGroup/primevue/usetoast';
import {
  i18n,
} from '@i18n';
import SemanticButtonGroup, {
  TSemanticButtonGroupProps,
} from '@qmulus/ui/components/button/semantic-button-group.vue';
import InputText from '@qmulus/ui/components/input-text/input-text.vue';
import InputTextarea from '@qmulus/ui/components/input-textarea/input-textarea.vue';
import ModalQmulus from '@qmulus/ui/components/modal-qmulus/modal-qmulus.vue';
import {
  SendFeedbackParams,
} from '@sentry/core';
import {
  EventHint,
} from '@sentry/vue';
import * as Sentry from '@sentry/vue';
import {
  computed,
  ref,
  shallowRef,
} from 'vue';
import {
  z,
} from 'zod';

import UploadFileDragAndDrop from '@/components/upload-file-drag-and-drop/upload-file-drag-and-drop.vue';
import {
  MAXIMUM_IMAGE_SIZE, MIME_GROUPS,
} from '@/constants/file-upload.ts';
import {
  useAppStore,
} from '@/store/pinia/app.ts';
import {
  EToastLife,
} from '@/types/toast.ts';
import {
  imageFileToUint8Array,
} from '@/utils/uint8-array.ts';

const toast = useToast();
const appStore = useAppStore();

const I18N_PREFIX = 'components/modal-bug-report/modal-bug-report';

const emit = defineEmits<{
  (e: 'close'): void,
}>();

const visible = shallowRef(true);

const feedbackForm = ref<SendFeedbackParams>({
  name: appStore.user?.displayName || '',
  email: appStore.user?.email || '',
  message: '',
});

const SendFeedbackParamsZod = z.object({
  name: z.string().min(1),
  email: z.string().email(),
  message: z.string().min(1),
});
const formValidationResult = computed(() => SendFeedbackParamsZod.safeParse(feedbackForm.value));
const formattedErrorMap = computed(() => formValidationResult.value.success ? undefined : formValidationResult.value.error.format());

const formAttachments = ref<EventHint['attachments']>(undefined);
async function onAttachmentSelection(files: File[]) {
  if (files.length === 0) {
    formAttachments.value = undefined;
  }

  const attachments: EventHint['attachments'] = [];
  for (const file of files) {
    attachments.push({
      filename: file.name,
      data: await imageFileToUint8Array(file),
    });
  }
  formAttachments.value = attachments;
}

const isSubmitting = shallowRef(false);
async function submit() {
  if (!formValidationResult.value.success) {
    return;
  }

  isSubmitting.value = true;
  Sentry.captureFeedback(
    {
      ...feedbackForm.value,
      url: window.location.toString(),
    },
    {
      attachments: formAttachments.value,
      includeReplay: true,
    },
  );
  toast.add({
    summary: i18n.global.t(`${I18N_PREFIX}.sendConfirmation.summary`),
    detail: i18n.global.t(`${I18N_PREFIX}.sendConfirmation.detail`),
    severity: 'success',
    life: EToastLife.ERROR, // The text is rather long, we give the user more time to read.
  });
  isSubmitting.value = false;
  emit('close');
}

const actionButtons = computed<TSemanticButtonGroupProps>(() => {
  return {
    cancel: {
      disabled: isSubmitting.value,
      onClick: () => emit('close'),
    },
    save: {
      label: i18n.global.t('generalTerm.send'),
      disabled: !formValidationResult.value.success,
      loading: isSubmitting.value,
      onClick: submit,
    },
  };
});
</script>
